import { useMemo, useState } from 'react';

import { Typography } from '@mui/material';
import { convertsCurrencyToCents } from '@npi/buddy-utils';
import { InvoiceStatus, PaymentAllocationStatus } from '@prisma/client';
import { GetSalesInvoice } from 'types/graphql';

import { useMutation } from '@redwoodjs/web';

import { Card } from 'src/components';
import { ConfirmationModal } from 'src/components/Modals';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { PAYMENT_METHODS } from 'src/constants/paymentmethods';
import { UPDATE_CREDIT_NOTE_STATUS } from 'src/graphql/mutations/updateCreditNoteStatus';
import { useUnallocatePayment } from 'src/hooks';
import { useCurrency } from 'src/hooks/useCurrency';
import {
    InvoicePaymentHistoryLog,
    invoicePaymentHistoryLogs,
    PaymentLogType,
} from 'src/utils/tableUtils';

import PaymentHistoryTable from './PaymentHistoryTable';
import PaymentModalContent from './PaymentModalContent';

interface InvoicePaymentHistoryProps {
    invoice?: GetSalesInvoice['invoice'];
    refetchInvoice: () => void;
}

const InvoicePaymentHistory = ({ invoice, refetchInvoice }: InvoicePaymentHistoryProps) => {
    const { currencySymbol } = useCurrency();
    const [paymentToDelete, setPaymentToDelete] = useState<InvoicePaymentHistoryLog>();
    const { handleUnallocatePayment: deletePayment, unallocatingPayment } = useUnallocatePayment(
        () => {
            return refetchInvoice();
        }
    );
    const creditNotes = invoice?.CreditNote;

    const [updateCreditNoteStatus, { loading: unallocatingCreditNote }] = useMutation(
        UPDATE_CREDIT_NOTE_STATUS,
        {
            onCompleted: ({ updateCreditNoteStatus: { message } }) => {
                showSuccessToast(message);
                refetchInvoice();
            },
            onError: (error) => {
                showErrorToast(error.message ?? 'Error Deleting the Reason');
            },
        }
    );

    const isDeletingPayment = useMemo(() => {
        return unallocatingPayment || unallocatingCreditNote;
    }, [unallocatingPayment, unallocatingCreditNote]);

    const allocations = useMemo(() => {
        return (invoice?.paymentAllocations ?? []).map((allocation) => {
            const { paymentMethod } = allocation;
            if (
                paymentMethod === PAYMENT_METHODS.BANK_TRANSFER.value ||
                paymentMethod === PAYMENT_METHODS.DIRECT_DEBIT.value
            ) {
                return {
                    ...allocation,
                    paymentMethod: PAYMENT_METHODS[paymentMethod].label,
                };
            }
            return {
                ...allocation,
                paymentMethod: PAYMENT_METHODS.CARD.label,
            };
        });
    }, [invoice]);

    const paymentHistoryTableLogs = useMemo(() => {
        return invoicePaymentHistoryLogs(allocations, creditNotes);
    }, [allocations, creditNotes]);

    const updatedInvoiceStatus = useMemo(() => {
        if (allocations.length > 1) {
            const numberOfAllocatedPayments = allocations.reduce((total, { status }) => {
                if (status === PaymentAllocationStatus.ALLOCATED) {
                    return total + 1;
                }
                return total;
            }, 0);
            if (numberOfAllocatedPayments === 1) {
                return InvoiceStatus.UNPAID;
            }
            return InvoiceStatus.PARTIALLY_PAID;
        }
        return InvoiceStatus.UNPAID;
    }, [allocations]);

    const onCancel = () => {
        setPaymentToDelete(null);
    };

    const onDeletePayment = async () => {
        const { buddyId, sageId, paymentType } = paymentToDelete.paymentId;

        if (paymentType === PaymentLogType.CREDIT_NOTE) {
            await updateCreditNoteStatus({
                variables: {
                    input: {
                        id: buddyId,
                        sageCreditNoteId: sageId,
                    },
                },
            });
        } else {
            const amountInCents = convertsCurrencyToCents(paymentToDelete?.paymentAmount ?? 0);
            await deletePayment({
                allocationId: buddyId,
                sagePaymentId: sageId,
                invoiceBalance: invoice?.totalOutstanding,
                amount: Math.round(amountInCents),
                status: updatedInvoiceStatus,
                date: new Date(),
            });
        }
        setPaymentToDelete(null);
    };

    return (
        <Card color="#1d1d1b" flex={1} gap="24px">
            <Typography fontSize="20px" fontWeight="500" lineHeight="30px">
                Invoice Payment History
            </Typography>

            <PaymentHistoryTable
                currency={currencySymbol}
                paymentLogs={paymentHistoryTableLogs}
                onPaymentToDelete={(payment) => {
                    return setPaymentToDelete(payment);
                }}
            />

            <ConfirmationModal
                isOpen={paymentToDelete?.paymentMethod?.length > 0}
                title="Delete Payment?"
                modalWidth="30%"
                confirmText={isDeletingPayment ? 'Deleting...' : 'Confirm'}
                message={
                    <PaymentModalContent
                        currency={currencySymbol}
                        invoiceStatus={invoice?.status}
                        paymentMethod={paymentToDelete?.paymentMethod}
                        createdDate={paymentToDelete?.paymentDate}
                        amountPaid={paymentToDelete?.paymentAmount}
                    />
                }
                onCancel={onCancel}
                onConfirm={onDeletePayment}
            />
        </Card>
    );
};
export default InvoicePaymentHistory;
